import React from 'react';
import { container } from 'tsyringe';
import { PelatihanPresenter } from '../../../../../data/presenters/PelatihanPresenter';
import 'react-toastify/dist/ReactToastify.css';
import { Pelatihan } from '../../../../../entities/Pelatihan';
import { SubMenuJenisPelatihan } from '../../../../../entities/MenuJenisPelatihan';
import { MenuJenisPelatihanPresenter } from '../../../../../data/presenters/MenuJenisPelatihanPresenter';
import { Jenjang } from '../../../../../entities/Jenjang';
import { JenjangPresenter } from '../../../../../data/presenters/JenjangPresenter';

interface IState {
  isLoading: boolean;
  jumlahPage: number;
  page: number;
  keyword: string;
  tahun: string;
  jenjang: string;
  jenis_pelatihan_id: string;
  totalPelatihan: number;
  pelatihan: Pelatihan[];
  subMenuJenisPelatihan: SubMenuJenisPelatihan[];
  jenjangList: Jenjang[];
}

interface InitialState {
  state: IState;
  setPage: Function;
  searchPelatihan: Function;
  filterTahun: Function;
  filterJenisPelatihan: Function;
  filterJenjang: Function;
  handleSearchPelatihan: Function;
  getSubMenuJenisPelatihan: Function;
  getJenjang: Function;
}

const initialState: InitialState = {
  state: {
    isLoading: false,
    jumlahPage: 0,
    page: 1,
    keyword: '',
    tahun: '',
    jenjang: '',
    jenis_pelatihan_id: '',
    totalPelatihan: 0,
    pelatihan: [],
    subMenuJenisPelatihan: [],
    jenjangList: [],
  },
  searchPelatihan: () => {},
  setPage: () => {},
  filterTahun: () => {},
  filterJenisPelatihan: () => {},
  getSubMenuJenisPelatihan: () => {},
  handleSearchPelatihan: () => {},
  filterJenjang: () => {},
  getJenjang: () => {},
};

const Context = React.createContext<InitialState>(initialState);
const { Provider: PelatihanProvider } = Context;

const Provider: React.FC = ({ children }) => {
  const keywordLS = localStorage.getItem('keywordPelatihan') || '';
  
  const [state, setState] = React.useState<IState>({
    ...initialState.state,
    keyword: keywordLS, // Set initial keyword from localStorage
  });

  const requestRef = React.useRef(0);

  const setLoading = (value: boolean) => {
    setState((prev) => ({ ...prev, isLoading: value }));
  };

  /* =====================================================
      FETCH DATA (ONLY ONE PLACE)
  ===================================================== */

  const getPelatihan = async (
    page: number,
    keyword: string,
    tahun: string,
    jenis_pelatihan_id: string,
    jenjang: string,
    event_id = null
  ) => {
    const requestId = ++requestRef.current;

    setLoading(true);

    try {
      const pelatihanPresenter = container.resolve(PelatihanPresenter);

      const data = await pelatihanPresenter.getAllPelatihan(
        page,
        12,
        keyword,
        tahun,
        jenis_pelatihan_id,
        jenjang,
        event_id
      );

      if (requestId !== requestRef.current) return;

      setState((prev) => ({
        ...prev,
        pelatihan: data,
        jumlahPage: data?.length ? Math.ceil(data[0].jumlahData / 12) : 0,
      }));
    } catch (error: any) {
      console.log('error pelatihan:', error);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  };

  // fetch data pelatihan
  React.useEffect(() => {
    getPelatihan(
      state.page,
      state.keyword,
      state.tahun,
      state.jenis_pelatihan_id,
      state.jenjang
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.page,
    state.keyword,
    state.tahun,
    state.jenis_pelatihan_id,
    state.jenjang,
  ]);

 
  const setPage = (value: number) => {
    setState((prev) => ({
      ...prev,
      page: value + 1,
    }));
  };

  const searchPelatihan = (value: string) => {
    setState((prevstate) => ({
      ...prevstate,
      keyword: value,
      page: 1,
    }));
    if (state.pelatihan.length > 0) {
      localStorage.removeItem('keywordPelatihan');
    }
  };

  const handleSearchPelatihan = () => {
    localStorage.removeItem('keywordPelatihan');
    setState((prevstate) => ({
      ...prevstate,
      pelatihan: [],
      jumlahPage: 0,
      page: 1,
    }));
  };

  const filterTahun = (value: string) => {
    setState((prev) => ({
      ...prev,
      tahun: value,
      page: 1,
    }));
  };

  const filterJenisPelatihan = (value: string) => {
    setState((prev) => ({
      ...prev,
      jenis_pelatihan_id: value,
      page: 1,
    }));
  };

  const filterJenjang = (value: string) => {
    setState((prev) => ({
      ...prev,
      jenjang: value,
      page: 1,
    }));
  };


  const getSubMenuJenisPelatihan = async () => {
    try {
      const presenter = container.resolve(MenuJenisPelatihanPresenter);

      const data = await presenter.getAllSubMenuJenisPelatihan(1, 10000000, '');

      setState((prevstate) => ({
        ...prevstate,
        subMenuJenisPelatihan: data,
      }));
    } catch (error: any) {
      console.log('error get Jenis Pelatihan:', error);
    }
  };

  const getJenjang = async () => {
    try {
      const presenter = container.resolve(JenjangPresenter);
      const data = await presenter.getAllJenjang();
      setState((prevstate) => ({
        ...prevstate,
        jenjangList: data,
      }));
    } catch (error: any) {
       console.log('error get Jenjang:', error);
    }
  };

  return (
    <PelatihanProvider
      value={{
        state,
        setPage,
        searchPelatihan,
        filterTahun,
        filterJenisPelatihan,
        getSubMenuJenisPelatihan,
        handleSearchPelatihan,
        filterJenjang,
        getJenjang,
      }}
    >
      {children}
    </PelatihanProvider>
  );
};

export const usePelatihanContext = () => React.useContext(Context);
// eslint-disable-next-line
export default {
  usePelatihanContext,
  Provider,
};
